import { Pronouns } from '../store/sageHomeApi';

interface TeamMemberDisplayOptions {
    usePreferred?: boolean;
    justInitials?: boolean;
    firstOnly?: boolean;
    initializeFirst?: boolean;
    initializeLast?: boolean;
}

/**
 * Formats a team member's name.
 * @param teamMember The team member object.
 * @param options Formatting options.
 * - usePreferred: Whether to use the preferred name if available.
 * - justInitials: Whether to return just the initials (overrides initializeFirst/Last/Middle).
 * - firstOnly: Whether to return only the first name, for components that pass in first name separately.
 * - initializeFirst: Whether to initialize the first name (e.g., "J." instead of "John").
 * - initializeLast: Whether to initialize the last name (e.g., "D." instead of "Doe").
 * @returns The formatted name as a string (e.g., "John Doe", "J. Doe", "J. D.").
 */
export const getTeamMemberDisplayName = (
    teamMember?: { firstName: string; lastName: string; preferredName?: string },
    options: TeamMemberDisplayOptions = {}
) => {
    const {
        usePreferred = true,
        firstOnly = false,
        justInitials = false,
        initializeFirst = false,
        initializeLast = false,
    } = options;

    if (!teamMember) {
        return '';
    }

    const { preferredName, firstName, lastName } = teamMember;

    const effectiveFirstName = usePreferred && preferredName ? preferredName : firstName;

    // If justInitials is true, initialize all names regardless of individual settings
    const shouldInitializeFirst = justInitials || initializeFirst;
    const shouldInitializeLast = justInitials || initializeLast;

    const firstPart =
        shouldInitializeFirst && effectiveFirstName
            ? effectiveFirstName.charAt(0).toLocaleUpperCase() + '.'
            : (effectiveFirstName ?? '');

    if (firstOnly) {
        return firstPart.trim();
    }

    const lastPart =
        shouldInitializeLast && lastName
            ? lastName.charAt(0).toLocaleUpperCase() + '.'
            : (lastName ?? '');

    return [firstPart, lastPart]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' ');
};

export const PronounLabel: Record<Pronouns, string> = {
    HE_HIM_HIS: 'He/Him',
    SHE_HER_HERS: 'She/Her',
    THEY_THEM_THEIR: 'They/Them',
};

const PersonalPronounMap: Record<Pronouns, string> = {
    HE_HIM_HIS: 'he',
    SHE_HER_HERS: 'she',
    THEY_THEM_THEIR: 'they',
};

const PossessivePronounMap: Record<Pronouns, string> = {
    HE_HIM_HIS: 'his',
    SHE_HER_HERS: 'her',
    THEY_THEM_THEIR: 'their',
};

const ReflexivePronounMap: Record<Pronouns, string> = {
    HE_HIM_HIS: 'himself',
    SHE_HER_HERS: 'herself',
    THEY_THEM_THEIR: 'themselves',
};

export type PronounType = 'personal' | 'possessive' | 'reflexive';
export const getPronoun = (pronoun?: Pronouns, type: PronounType = 'personal') => {
    if (!pronoun) return undefined;

    switch (type) {
        case 'possessive':
            return PossessivePronounMap[pronoun];
        case 'reflexive':
            return ReflexivePronounMap[pronoun];
        case 'personal':
        default:
            return PersonalPronounMap[pronoun];
    }
};
